import Downshift, { type ControllerStateAndHelpers } from "downshift";
import React, { useCallback, useState } from "react";
import { debounce } from "ts-debounce";
import useAsyncEffect from "use-async-effect";
import { emailRegex } from "../utils/emailRegex";
import Styles from "./RecipientInput.module.css";

//based on https://codesandbox.io/s/github/kentcdodds/downshift-examples/tree/master/?module=/src/downshift/other-examples/gmail/index.js&moduleview=1&file=/src/downshift/other-examples/gmail/index.js

type RecipientProps = React.PropsWithChildren<{
  isValid?: boolean;
  onRemove: () => void;
}>;

const Recipient: React.FC<RecipientProps> = ({
  onRemove,
  children,
  isValid = true,
}) => {
  return (
    <div className={`${Styles.recipient} ${!isValid ? Styles.invalid : ""}`}>
      {children}
      <div
        className={`${Styles.addRecipientButton} hoylu-ui-icons-x`}
        onClick={onRemove}
      />
    </div>
  );
};

type RecipientInputProps = {
  strings: any;
  onChange: (selection: Contact[]) => void;
  fetchContacts: IFetchContacts;
  selectedContacts: Contact[];
};

export class RecipientInput extends React.Component<
  RecipientInputProps,
  { selectedContacts: Contact[] }
> {
  input = React.createRef<HTMLInputElement>();

  handleChange = (
    selectedContact: Contact | null,
    downshift: ControllerStateAndHelpers<Contact>,
  ) => {
    if (selectedContact) {
      downshift.reset();
      this.props.onChange([...this.props.selectedContacts, selectedContact]);
    }
  };

  removeContact(contact: Contact) {
    this.input.current?.focus();
    this.props.onChange(
      this.props.selectedContacts.filter((c) => c !== contact),
    );
  }

  handleInputKeyDown = ({
    event,
    isOpen,
    selectHighlightedItem,
    highlightedIndex,
    reset,
    inputValue,
  }: InputKeyDownProps) => {
    if (isOpen && ["Tab", ",", ";"].includes(event.key) && inputValue) {
      event.preventDefault();
      if (highlightedIndex !== null) {
        selectHighlightedItem();
      } else {
        reset();
        this.props.onChange([
          ...this.props.selectedContacts,
          makeContact(inputValue),
        ]);
      }
    }
  };

  itemToString = (i: Contact | null) => {
    return i ? (i.name === i.email ? i.name : `${i.name} (${i.email})`) : "";
  };

  render() {
    const { selectedContacts } = this.props;
    return (
      <Downshift
        defaultHighlightedIndex={0}
        itemToString={this.itemToString}
        onChange={this.handleChange}
        selectedItem={null}
      >
        {({
          getInputProps,
          getItemProps,
          getMenuProps,
          isOpen,
          highlightedIndex,
          selectHighlightedItem,
          setHighlightedIndex,
          reset,
          inputValue,
          clearItems,
          setItemCount,
        }) => (
          <div className={Styles.recipientInputContainer}>
            <div className={Styles.selectedContacts}>
              {selectedContacts.map((c) => (
                <Recipient
                  isValid={c.email.match(emailRegex) != null}
                  key={c.id}
                  onRemove={() => this.removeContact(c)}
                >
                  {this.itemToString(c)}
                </Recipient>
              ))}
            </div>
            <input
              {...getInputProps({
                ref: this.input,
                onKeyDown: (event) =>
                  this.handleInputKeyDown({
                    event,
                    selectHighlightedItem,
                    highlightedIndex,
                    isOpen,
                    reset,
                    inputValue,
                  }),
                placeholder: this.props.strings.ENTER_USER,
                className: Styles.recipientInput,
              })}
            />
            {isOpen && (
              <FetchContacts
                fetchContacts={this.props.fetchContacts}
                omitContacts={selectedContacts}
                onLoaded={({ contacts }) => {
                  clearItems();
                  if (contacts) {
                    if (
                      contacts.length === 0 &&
                      isNewEmail(inputValue, selectedContacts)
                    ) {
                      setItemCount(1);
                      setHighlightedIndex(0);
                      return;
                    }

                    // FIXME: What is even going on here? (1) what is the point of using a non-null assertion on `null`, and (2) `setHighlightedIndex` only takes a number type so passing null doesn't even make sense to do. Should we perhaps use `-1` instead of `null` here? Or conditionally only set the highlighted index when there are actual contacts to highlight? Based on the condition above it seems we might be trying to set the highlighted index to `0` regardless.
                    setHighlightedIndex(contacts.length ? 0 : null!);
                    setItemCount(contacts.length);
                  }
                }}
                searchValue={inputValue}
              >
                {({ loading, contacts, error }: FetchContactsState) => (
                  <div
                    {...getMenuProps({
                      className: Styles.contactListContainer,
                    })}
                  >
                    {loading ? (
                      <div className={Styles.listOption}>
                        {this.props.strings.LOADING}
                      </div>
                    ) : error ? (
                      <div className={Styles.listOption}>
                        {this.props.strings.FAILED_TO_LOAD_USERS}
                      </div>
                    ) : contacts.length ? (
                      <ContactList
                        contacts={contacts}
                        getItemProps={getItemProps}
                        highlightedIndex={highlightedIndex}
                      />
                    ) : (
                      <>
                        {isNewEmail(inputValue, selectedContacts) && (
                          <div
                            {...getItemProps({
                              item: makeContact(inputValue!),
                              index: 0,
                              className: Styles.newEmail,
                            })}
                          >
                            {`${this.props.strings.USE_THIS_ADDRESS}: ${inputValue}`}
                          </div>
                        )}
                        <div className={Styles.listOption}>
                          {this.props.strings.NO_RESULTS_FOUND}
                        </div>
                      </>
                    )}
                  </div>
                )}
              </FetchContacts>
            )}
          </div>
        )}
      </Downshift>
    );
  }
}

function isNewEmail(inputValue: string | null, selectedContacts: Contact[]) {
  return (
    inputValue?.match(emailRegex) &&
    !selectedContacts.some(
      (c) =>
        inputValue.localeCompare(c.email, undefined, {
          sensitivity: "base",
          usage: "search",
        }) === 0,
    )
  );
}

type InputKeyDownProps = {
  event: React.KeyboardEvent<HTMLInputElement>;
} & Pick<
  ControllerStateAndHelpers<Contact>,
  | "selectHighlightedItem"
  | "highlightedIndex"
  | "isOpen"
  | "reset"
  | "inputValue"
>;

type ContactListProps = {
  highlightedIndex: number | null;
  contacts: Contact[];
} & Pick<ControllerStateAndHelpers<Contact>, "getItemProps">;

const ContactList: React.FC<ContactListProps> = ({
  highlightedIndex,
  getItemProps,
  contacts,
}) => {
  return (
    <ul>
      {contacts.map((item, index) => (
        <li
          key={item.id}
          {...getItemProps({
            item,
            index,
            className: `${Styles.contactList} ${
              highlightedIndex === index ? Styles.highlighted : ""
            }`,
          })}
        >
          <div>{item.name}</div>
          {item.name === item.email || (
            <div className={Styles.contactListItem}>{item.email}</div>
          )}
        </li>
      ))}
    </ul>
  );
};

type FetchContactsProps = {
  searchValue: string | null;
  omitContacts: Contact[];
  onLoaded: (
    state: Partial<Pick<FetchContactsState, "contacts" | "error">>,
  ) => void;
  limit?: number;
  fetchContacts: IFetchContacts;
  children?: (state: FetchContactsState) => React.ReactNode;
};
type FetchContactsState = { loading: boolean; error: any; contacts: Contact[] };

const LOADING: FetchContactsState = {
  loading: true,
  error: null,
  contacts: [],
};
const idle = (contacts: Contact[]): FetchContactsState => ({
  loading: false,
  error: null,
  contacts,
});
const fetchError = (error: any): FetchContactsState => ({
  loading: false,
  error,
  contacts: [],
});

const FetchContacts: React.FC<FetchContactsProps> = ({
  children,
  fetchContacts,
  searchValue,
  omitContacts,
  limit,
  onLoaded,
}) => {
  const [state, setState] = useState<FetchContactsState>(idle([]));
  const fetch = useCallback(debounce(fetchContacts, 300), [fetchContacts]);

  useAsyncEffect(
    async (isActive) => {
      setState(LOADING);
      try {
        const {
          response: { data: contacts },
        } = await fetch(searchValue?.toLowerCase() ?? "", {
          omitContacts,
          limit,
        });
        if (!isActive()) {
          return;
        }
        onLoaded({ contacts });
        setState(idle(contacts));
      } catch (error) {
        if (!isActive()) {
          return;
        }
        onLoaded({ error });
        setState(fetchError(error));
      }
    },
    [searchValue, omitContacts, limit, fetch],
  );

  return <>{children?.(state)}</>;
};

export type Contact = {
  name: string;
  email: string;
  id: string;
};

export const makeContact = (inputValue: string): Contact => ({
  id: inputValue.toLowerCase(),
  email: inputValue,
  name: inputValue,
});

type IFetchContacts = (
  searchValue: string,
  props?: FetchProps,
) => Promise<FetchResponse>;

type FetchResponse = {
  response: {
    data: Contact[];
    requestId?: number;
  };
};

export type FetchProps = {
  omitContacts?: Contact[];
  limit?: number;
  requestId?: number;
  allContacts?: Contact[];
};
